import { Category, type DataPoint, FileCategory, type ProviderFile, toDataPoint } from "fileParsing/types";
import { AlgoStep } from "fileParsing/wrangler/types";
import JSZip from "jszip";
import Papa from "papaparse";
import type { LanguageTypes } from "store/language/types";

const Example1 = new URL("../../public/image_1.jpg", import.meta.url).href;
const Example2 = new URL("../../public/image_2.jpg", import.meta.url).href;
const Example3 = new URL("../../public/image_3.jpg", import.meta.url).href;

const firstNames = [
  "Alexander",
  "Andrea",
  "Andreas",
  "Angelika",
  "Anja",
  "Anna",
  "Barbara",
  "Ben",
  "Bernd",
  "Berta",
  "Bertha",
  "Birgit",
  "Brigitte",
  "Carl",
  "Christa",
  "Christian",
  "Christina",
  "Claudia",
  "Curt",
  "Daniel",
  "Dennis",
  "Dieter",
  "Edith",
  "Elfriede",
  "Elias",
  "Elisabeth",
  "Elke",
  "Elsa",
  "Emilia",
  "Emily",
  "Emma",
  "Erika",
  "Erna",
  "Ernst",
  "Felix",
  "Finn",
  "Frank",
  "Frieda",
  "Friedrich",
  "Gabriele",
  "Gerda",
  "Gerhard",
  "Gertrud",
  "Gisela",
  "Gustav",
  "Günter",
  "Hannah",
  "Hans",
  "Heike",
  "Heinrich",
  "Heinz",
  "Helga",
  "Helmut",
  "Herbert",
  "Hermann",
  "Hertha",
  "Hildegard",
  "Horst",
  "Ilse",
  "Inge",
  "Ingeborg",
  "Ingrid",
  "Irmgard",
  "Jan",
  "Jannik",
  "Jennifer",
  "Jessika",
  "Jonas",
  "Julia",
  "Jörg",
  "Jürgen",
  "Karin",
  "Karl",
  "Katharina",
  "Katrin",
  "Klaus",
  "Käthe",
  "Lara",
  "Laura",
  "Lea",
  "Lena",
  "Leon",
  "Leonie",
  "Lieselotte",
  "Lilli",
  "Lina",
  "Lisa",
  "Luca",
  "Luis",
  "Lukas",
  "Manfred",
  "Marcel",
  "Margarete",
  "Maria",
  "Marie",
  "Marion",
  "Markus",
  "Marta",
  "Martin",
  "Martina",
  "Matthias",
  "Max",
  "Maximilian",
  "Melanie",
  "Mia",
  "Michael",
  "Michelle",
  "Monika",
  "Nadine",
  "Nicole",
  "Niklas",
  "Noah",
  "Oliver",
  "Otto",
  "Patrick",
  "Paul",
  "Peter",
  "Petra",
  "Philipp",
  "Renate",
  "Rolf",
  "Sabine",
  "Sabrina",
  "Sandra",
  "Sarah",
  "Sebastian",
  "Sofia",
  "Stefan",
  "Stefanie",
  "Susanne",
  "Tanja",
  "Thomas",
  "Tim",
  "Tobias",
  "Tom",
  "Torsten",
  "Ursula",
  "Ute",
  "Uwe",
  "Walter",
  "Werner",
  "Wilhelm",
  "Wolfgang",
];
const lastNames = [
  "Albrecht",
  "Arnold",
  "Bachmann",
  "Barth",
  "Bauer",
  "Baumann",
  "Beck",
  "Becker",
  "Berg",
  "Berger",
  "Bergmann",
  "Beyer",
  "Brandt",
  "Braun",
  "Brunner",
  "Busch",
  "Böhm",
  "Demir",
  "Dietrich",
  "Engel",
  "Ernst",
  "Fischer",
  "Frank",
  "Franke",
  "Franz",
  "Frey",
  "Friedrich",
  "Fritz",
  "Fuchs",
  "Graf",
  "Gruber",
  "Günther",
  "Haas",
  "Hahn",
  "Hansen",
  "Hartmann",
  "Heinrich",
  "Hermann",
  "Herrmann",
  "Herzog",
  "Hofer",
  "Hoffmann",
  "Hofmann",
  "Horn",
  "Huber",
  "Jahn",
  "Jansen",
  "Jung",
  "Jäger",
  "Kaiser",
  "Kaufmann",
  "Kaya",
  "Keller",
  "Kern",
  "Klein",
  "Koch",
  "Kramer",
  "Kraus",
  "Krause",
  "Krämer",
  "Krüger",
  "Kuhn",
  "Kunz",
  "Köhler",
  "König",
  "Kühn",
  "Lang",
  "Lange",
  "Langer",
  "Lehmann",
  "Lenz",
  "Lindner",
  "Lorenz",
  "Ludwig",
  "Maier",
  "Martin",
  "Maurer",
  "Maus",
  "Mayer",
  "Meier",
  "Meyer",
  "Michel",
  "Miller",
  "Moser",
  "Mueller",
  "Möller",
  "Müller",
  "Neumann",
  "Nowak",
  "Otto",
  "Paul",
  "Peter",
  "Peters",
  "Petersen",
  "Pfeiffer",
  "Pohl",
  "Reinhardt",
  "Richter",
  "Riedel",
  "Ritter",
  "Rose",
  "Roth",
  "Sauer",
  "Schmid",
  "Schmidt",
  "Schmitt",
  "Schmitz",
  "Schneider",
  "Scholz",
  "Schreiber",
  "Schröder",
  "Schubert",
  "Schulte",
  "Schulz",
  "Schulze",
  "Schumacher",
  "Schuster",
  "Schwarz",
  "Schäfer",
  "Seidel",
  "Seifert",
  "Simon",
  "Sommer",
  "Stein",
  "Steiner",
  "Thomas",
  "Vogel",
  "Vogt",
  "Voigt",
  "Wagner",
  "Walter",
  "Weber",
  "Weiss",
  "Wenzel",
  "Werner",
  "Wilhelm",
  "Winkler",
  "Winter",
  "Wolf",
  "Yildirim",
  "Yildiz",
  "Yilmaz",
  "Ziegler",
  "Zimmermann",
];
const loremIpsum =
  ":) :( :D 😀 😊 \n \n lol accusamus accusantium adipisci alias aliquam aliquid amet animi aperiam architecto asperiores aspernatur assumenda at atque aut autem beatae blanditiis commodi consectetur consequatur consequuntur corporis corrupti culpa cumque cupiditate debitis delectus deleniti deserunt dicta dignissimos distinctio dolor dolore dolorem doloremque dolores doloribus dolorum ducimus dunt ea eaque earum eius eligendi enim eos error esse est et eum eveniet ex excepturi exercitationem expedita explicabo facere facilis fuga fugiat fugit harum hic id illo illum impedit in inci inea inventore ipsa ipsam ipsum iste iure iusto labore laborum laudantium libero magnam magni maiores maxime minima minus modi molestiae molestias mollitia nam natus necessitatibus nemo neque nesciunt nihil nisi nobis non nostrum nulla numquam obcaecati odio odit officia officiis omnis optio pariatur perferendis perspiciatis placeat porro possimus praesentium provident quae quaerat quam quasi qui quia quibusdam quidem quis quis quisquam quo quod quos ratione recusandae reiciendis rem repellendus rerum saepe sapiente sed sed sequi similique sint sit soluta sunt suscipitlaboriosam tempora temporibus tenetur totam ullam unde ut ut vel velit veniam veritatis vero vitae voluptate".split(
    " ",
  );
const cities = [
  "Berlin",
  "Hamburg",
  "Munich",
  "Cologne",
  "Frankfurt am Main",
  "Düsseldorf",
  "Leipzig",
  "Dortmund",
  "Essen",
  "Bremen",
  "Dresden",
  "Hanover",
  "Duisburg",
  "Bochum",
  "Bielefeld",
  "Bonn",
  "Augsburg",
  "Brunswick",
  "Aachen",
  "Chemnitz",
  "Darmstadt",
];
const places = {
  Fraunhofer: {
    coordinates: "(49.87437294069707, 8.659378002596865)",
    name: "Fraunhofer IGD",
  },
  Home: {
    coordinates: "(50.11184990835545, 8.681563671706298)",
    name: "Home - Frankfurt",
  },
  Random: {
    coordinates: "(50.075401350178055, 8.661346280131058)",
  },
  Vacation: {
    coordinates: "(21.338674025281307, -157.83818212709647)",
    name: "Honululu - Hawaii",
  },
};

const pictures = [Example1, Example2, Example3];

const timeLabels = [
  "Ende",
  "PROFILECREATION_TIME",
  "endTimeNanos",
  "timestamp",
  "modifiedTimeMillis",
  "creation_timestamp",
  "updated_at",
  "next_bill_date",
  "published",
  "close_datetime_local",
  "timestamp_ms",
  "last_modified_by_any_user",
  "Beginn",
  "RetrieveDate",
  "Timestamp of Last Modification",
  "LastUpdatedTimeInUTC",
  "lastUpdatedDate",
  "last_modified_timestamp",
  "taken_timestamp",
  "Timestamp (UTC)",
  "eventDate",
  "ModificationTime",
  "userEditedTimestampUsec",
  "purchaseDate",
  "Timestamp",
  "created",
  "Time",
  "time_spent_in_seconds",
  "LastUpdatedLocalTime",
  "Track Last Updated Time",
  "LastModifiedDate",
  "EndDateTime",
  "Track Creation Date",
  "created_at",
  "creationDate",
  "albumReleaseDate",
  "timestampMs",
];

// Filename, FileType, Folder
const facebookBlueprint = [
  ["your_address_books.json", "Text", "about_you"],
  //["no-data.txt", "Text", "ads"],
  //["no-data.txt", "Text", "apps_and_websites"],
  //["no-data.txt", "Text", "comments"],
  //["no-data.txt", "Text", "events"],
  //["no-data.txt", "Text", "following_and_followers"],
  ["friends.json", "Text", "friends"],
  //["received_friend_requests.json", "Text", "friends"],
  ["sent_friend_requests.json", "Text", "friends"],
  //["your_group_membership_activity.json", "Text", "groups"],
  ["pages.json", "Text", "likes_and_reactions"],
  ["posts_and_comments.json", "Text", "likes_and_reactions"],
  //["no-data.txt", "Text", "location"],
  //["no-data.txt", "Text", "marketplace"],
  //["no-data.txt", "Text", "other_activity"],
  //["no-data.txt", "Text", "pages"],
  ["0.json", "Text", "photos_and_videos"],
  ["your_posts_1.json", "Text", "posts"],
  ["profile_information.json", "Text", "profile_information"],
  ["profile_update_history.json", "Text", "profile_information"],
  //["no-data.txt", "Text", "saved_items_and_collections"],
  ["your_search_history.json", "Text", "search_history"],
  ["account_activity.json", "Text", "security_and_login_information"],
  ["administrative_records.json", "Text", "security_and_login_information"],
  ["logins_and_logouts.json", "Text", "security_and_login_information"],
  ["login_protection_data.json", "Text", "security_and_login_information"],
  ["where_you're_logged_in.json", "Text", "security_and_login_information"],
  ["location.json", "Text", "location_history"],
  //["no-data.txt", "Text", "stories"],
  //["no-data.txt", "Text", "your_places"]
];

// TODO: move to translations json
const translation = {
  en: {
    ChatWith: "Chat with ",
    name: "Your full name is ",
    first: "\nYour first name is ",
    middle: "\nYour middle name is ",
    last: "\nYour last name is ",
    current: "\n\nYour current email is ",
    previousemail: "\nYour previous email is ",
    pending: "\nYour pending email is: none",
    ad: "\nYour ad account email is: none ",
    city: "\nYour place of residence is ",
    birthday: "\n\nYour birthday is on ",
    gender: "\n\nYour gender is ",
    pronoun: "\nYour pronoun is ",
    previousnames: "\n\nYour previous names are ",
    other: "\nYour other names are ",
    family: "\nYour family members are ",
    and: ", and ",
    male: "Male",
    female: "Female",
    non: "Gender Non-confirming",
    liked: "You liked the page of ",
    AddressBook: "Address Book",
    Friend: "Friend",
    PageLikes: "Page Likes",
    post: "You liked a post of ",
    Postings: "Postings",
    YourPosts: "Your Posts",
    posted: "You posted:\n",
    Profile: "Profile Information",
    placeChange: " changed their place of residence to ",
    ProfileUpdate: "Profile Updates",
    searched: "You searched for",
    signin: "You signed in to www.facebook.com",
    signout: "You signed out off www.facebook.com",
    Security: "Security: Login and Logout",
    Login: "Security: Login Protection",
    Location: "Your Location",
  },
  de: {
    ChatWith: "Chat with ",
    name: "Dein ganzer Name ist ",
    first: "\nDein Vorname ist ",
    middle: "\nDein mittlerer Name ist ",
    last: "\nDein Nachname ist ",
    current: "\n\nDeine aktuelle Email ist ",
    previousemail: "\nDeine Vorherige Email ist ",
    pending: "\nDeine ausstehende E-Mail lautet: keine",
    ad: "\nDie E-Mail-Adresse deines Werbekontos lautet: keine",
    city: "\nDein Wohnort lautet ",
    birthday: "\n\nDein Geburtsdatum ist am ",
    gender: "\n\nDein Geschlecht ist ",
    pronoun: "\nDeine Pronomen sind ",
    previousnames: "\n\nDeine vorherigen Namen sind: ",
    other: "\nDeine anderen Namen sind: ",
    family: "\nDeine Familien Mitglider sind ",
    and: ", und ",
    male: "Männlich",
    female: "Weiblich",
    non: "Divers",
    liked: "Du hast die Seite gelikt von ",
    AddressBook: "Adressbuch",
    Friend: "Freunde",
    PageLikes: "Seiten Likes",
    post: "Du hast einen Beitrag gelikt von ",
    Postings: "Beiträge",
    YourPosts: "Deine Beiträge",
    posted: "Du hast veröffentlicht.:\n",
    Profile: "Profil Informationen",
    placeChange: " hat ihren Wohnsitz verlegt nach ",
    ProfileUpdate: "Profil Änderungen",
    searched: "Du suchstest nach",
    signin: "Sie haben sich bei www.facebook.com angemeldet.",
    signout: "Sie haben sich bei www.facebook.com abgemeldet.",
    Security: "Sicherheit: Anmeldung und Abmeldung",
    Login: "Sicherheit: Login-Schutz",
    Location: "dein Standort",
  },
};

type Person = {
  name: string;
  age: number;
  gender: string;
  email: string;
  birthday: Date;
  city: string;
};

export default class DataGenerator {
  static instance = new DataGenerator();
  s = NaN;

  // uniform distribution between 0 and 1
  random(): number {
    this.s = Math.sin(this.s) * 10000;
    return this.s - Math.floor(this.s);
  }

  random_array<T>(array: T[]): T {
    return array[Math.floor(this.random() * (array.length - 1))];
  }

  // Standard Normal variate using Box-Muller transform.
  randn_bm(min: number, max: number, skew: number) {
    if (min === max) return min;
    let u = 0,
      v = 0;
    while (u === 0) u = this.random(); //Converting [0,1) to (0,1)
    while (v === 0) v = this.random();
    let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);

    num = num / 10.0 + 0.5; // Translate to 0 -> 1
    if (num > 1 || num < 0) num = this.randn_bm(min, max, skew); // resample between 0 and 1 if out of range
    num = Math.pow(num, skew); // Skew
    num *= max - min; // Stretch to fill range
    num += min; // offset to min
    return num;
  }

  getString(len: number) {
    const res = [];
    for (let i = 0; i < len; ++i) {
      res.push(loremIpsum[Math.floor(this.random() * (loremIpsum.length - 1))]);
    }
    return res.join(" ");
  }

  getDate(diffTime: number) {
    const toHours = (d: number) => Math.floor((d / (1000 * 60 * 60)) % 24);
    const toLocalTime = (d: Date) => d.getTime() - d.getTimezoneOffset() * 60000;
    const ticks_day = 1000 * 60 * 60 * 24;
    const startDate_intern = new Date(2004, 1).getTime() - 60 * 60 * 1000;
    //Day
    const newday = this.random() * diffTime;
    const current_day = newday - 60 * 60 * 1000 - (newday % ticks_day);

    //Time
    const newRandomTime = this.random() * diffTime;
    const current_hour = new Date(newRandomTime).getHours();
    const current_minutes = new Date(newRandomTime).getMinutes();
    const current_seconds = new Date(newRandomTime).getSeconds();
    const newtime = current_hour * 60 * 60 * 1000 + current_minutes * 60 * 1000 + current_seconds * 1000;

    let result = startDate_intern + current_day + newtime;

    // person should be sleeping, meaning less activity between 0 and 8 so we skip them
    const hours = toHours(toLocalTime(new Date(result)));
    if (hours < 7 && this.random() > 0.08) result += 1000 * 60 * 60 * (6 + 2 * this.random());

    return result;
  }

  getName(): string {
    return this.random_array(firstNames) + " " + this.random_array(lastNames);
  }

  randomDate(start: Date, end: Date) {
    return new Date(start.getTime() + this.random() * (end.getTime() - start.getTime()));
  }

  static async generateZip(seed = 0): Promise<Blob> {
    return this.instance.generateZip(seed);
  }

  async generateZip(seed = 0 /*, lang: LanguageTypes = 'en'*/): Promise<Blob> {
    if (seed === 0) {
      seed = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
    } else {
      seed = Math.floor(seed);
    }
    this.s = seed + 1;
    // const t = translation[lang];
    const name = this.getName();

    const range = (n = Math.floor(this.randn_bm(5, 100, 1))) => [...Array(n).keys()];
    const rnd = () => this.random();
    const rndInt = (n = Number.MAX_SAFE_INTEGER) => Math.floor(this.random() * n);
    const rndDate = (y: number) =>
      this.randomDate(
        new Date(Date.now() - (1000 * 3600 * 24 * 365 * y + 1)),
        new Date(Date.now() - 1000 * 3600 * 24 * 365),
      );
    const addTime = (date: Date, n: number) => new Date(date.getTime() + n);
    const email = (name: string) => `${name.toLowerCase().replaceAll(" ", "@")}.com`;
    const rndLabel = (n: number) => timeLabels[n % timeLabels.length];

    const toJSON = (data: unknown) => Promise.resolve(JSON.stringify(data));
    const toCSV = (data: unknown) => Promise.resolve(Papa.unparse(data as object[]));

    // Generate trees
    const sampleData: {
      name: string;
      data: unknown;
      toText: (data: unknown) => Promise<string>;
    }[] = [
      {
        name: "CSV/multipleTimestamps/subscriptions.csv",
        data: ((n) =>
          range().map((subscriptionId) =>
            ((date) => ({
              subscriptionId,
              itemID: rndInt(),
              [rndLabel(n)]: date.toUTCString(),
              [rndLabel(n + 1)]:
                rnd() > 0.3
                  ? ((d) => `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`)(
                      addTime(date, 1000 * 3600 * 24 * 90),
                    )
                  : "",
              [rndLabel(n + 2)]: rnd() > 0.2 ? addTime(date, -rndInt(30)).getTime() : "",
            }))(rndDate(5)),
          ))(rndInt()),
        toText: toCSV,
      },
      {
        name: "CSV/noTimestamp/permissions.csv",
        data: range().map((id) => ({
          id,
          name: this.getName(),
          ...((rnd) => ({
            read: rnd > 0.1 ? true : false,
            comment: rnd > 0.4 ? true : false,
            write: rnd > 0.7 ? 1 : 0,
            owner: rnd > 0.95 ? 1 : 0,
          }))(rnd()),
        })),
        toText: toCSV,
      },
      {
        name: "JSON/objectWithTimestamps/file-meta.json",
        data: {
          starred: false,
          viewers_can_download: true,
          editors_can_edit_access: true,
          ...((date) => ({
            last_modified_by_any_user: addTime(date, 1000 * 3600 * 24 * 60).toISOString(),
            last_modified_by_me: addTime(date, 1000 * 3600 * 24 * 30).toISOString(),
            content_last_modified: addTime(date, 1000 * 3600 * 24 * 60).toISOString(),
            created: date.toISOString(),
          }))(rndDate(3)),
          permissions: [
            {
              id: `${rndInt()}`,
              name,
              type: "user",
              role: "owner",
              kind: "drive#permission",
              photo_link: "https://www.example.com/photo.png",
              self_link: "https://www.example.com/photo.png",
              email_address: email(name),
              domain: "gmail.com",
              etag: `"${rndInt()}"`,
              deleted: false,
            },
          ],
        },
        toText: toJSON,
      },
      {
        name: "JSON/objectWithoutTimestamps/single-file-permissions.json",
        data: {
          id: rndInt(),
          name: this.getName(),
          ...((rnd) => ({
            read: rnd > 0.1 ? true : false,
            comment: rnd > 0.4 ? true : false,
            write: rnd > 0.7 ? 1 : 0,
            owner: rnd > 0.95 ? 1 : 0,
          }))(rnd()),
        },
        toText: toJSON,
      },
      {
        name: "JSON/arrayOfStrings/ads_interests.json",
        data: timeLabels
          .map<[number, string]>((l) => [rndInt(20), l])
          .filter(([x]) => x <= 10)
          .sort(([x], [y]) => x - y)
          .map(([, l]) => l),
        toText: toJSON,
      },
      {
        name: "JSON/arrayOfObjects/files.json",
        data: ((n) =>
          range().map((id) => ({
            starred: false,
            viewers_can_download: true,
            editors_can_edit_access: true,
            ...((date) => ({
              [rndLabel(n)]: addTime(date, 1000 * 3600 * 24 * 60).getTime() * 1000 * 1000,
              [rndLabel(n + 1)]: addTime(date, 1000 * 3600 * 24 * 30).getTime() * 1000,
              [rndLabel(n + 2)]: addTime(date, 1000 * 3600 * 24 * 60).getTime() * 1,
              [rndLabel(n + 3)]: date.toUTCString(),
            }))(rndDate(3)),
            permissions: {
              id: `${id}`,
              name,
              type: "user",
              role: "owner",
              kind: "drive#permission",
              photo_link: `https://www.example.com/photo-${id}.png`,
              self_link: `https://www.example.com/photo-${id}.png`,
              email_address: email(name),
              domain: "gmail.com",
              etag: `"${rndInt()}"`,
              deleted: false,
            },
          })))(rndInt()),
        toText: toJSON,
      },
      {
        name: "JSON/arrayOfObjectWithSubobjects/mobile_devices.json",
        data: ((n) =>
          range().map(() =>
            ((date) => ({
              os: rnd() >= 0.5 ? "Android" : "iOS",
              [rndLabel(n)]: date.getTime() * 1000,
              advertiser_id: `${rndInt()}`,
              push_token: {
                disabled: false,
                [rndLabel(n + 1)]: addTime(date, 1000 * 3600 * 24 * 365 * 3),
                [rndLabel(n + 2)]: addTime(date, 1000 * 3600 * 24 * 365),
                app_version: `${rndInt(999)}.0.0.0.${rndInt(99)}`,
                locale: "de_DE",
                token: "********************",
                device_id: `${rndInt()}`,
              },
              device_locale: "de_DE",
            }))(rndDate(7)),
          ))(rndInt()),
        toText: toJSON,
      },
      {
        name: "JSON/arrayOfNumbers/datr_cookie_info.json",
        data: { datr_stats: range().map(() => rndInt()) },
        toText: toJSON,
      },
      {
        name: "JSON/emptyObject/empty.json",
        data: {},
        toText: toJSON,
      },
      {
        name: "JSON/arrayWithManyEntries/messages.json",
        data: ((other, startDate) => ({
          participants: [
            {
              name,
            },
            {
              name: other,
            },
          ],
          messages: range(Math.floor(this.randn_bm(10 /*0000*/, 200 /*000*/, 1))).map(() => ({
            sender_name: rnd() > 0.5 ? other : name,
            timestamp_ms: addTime(startDate, rndInt(1000 * 3600 * 24 * 365 * 10)).getTime(),
            content: this.getString(this.randn_bm(1, 200, 7)),
            type: "Generic",
            is_unsent: false,
          })),
          title: other,
          is_still_participant: true,
          thread_type: "Regular",
          thread_path: `inbox/${other.toLowerCase().replaceAll(" ", "")}`,
        }))(this.getName(), rndDate(10)),
        toText: toJSON,
      },
      {
        name: "JSON/arrayWithVaryingFields/comments.json",
        data: {
          comments: range().map(() =>
            ((date) => ({
              timestamp: date.getTime(),
              data: [
                {
                  comment: {
                    timestamp: date.getTime(),
                    comment: [
                      this.getString(this.randn_bm(1, 200, 7)),
                      ...range()
                        .map(
                          () =>
                            ["😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃", "🫠", "😉", "😊", "😇"][
                              rndInt(100) % 13
                            ],
                        )
                        .filter(
                          () => rnd() > 0.2,
                        ) /*.map(e => `\\${e.charCodeAt(0).toString(16)}\\${e.charCodeAt(1).toString(16)}`)*/,
                    ].join(""),
                    author: name,
                    ...(rnd() > 0.5 ? {} : { group: "Beispielgruppe" }),
                    ...(rnd() > 0.5 ? {} : { likes: rndInt(100) }),
                    ...(rnd() > 0.5
                      ? {}
                      : {
                          reactions: range()
                            .map(() => ["Like", "Love", "Haha", "Wow", "Sad", "Angry"][rndInt(100) % 6])
                            .filter(() => rnd() > 0.5),
                        }),
                  },
                },
              ],
              title: `${name} hat ${this.getName()} Beitrag kommentiert.`,
            }))(rndDate(7)),
          ),
        },
        toText: toJSON,
      },
      {
        name: "JSON/twitterPrefix/posts.js",
        data: {
          posts: range().map(() =>
            ((date) => ({
              timestamp: date.getTime(),
              data: [
                {
                  post: {
                    timestamp: date.getTime(),
                    post: this.getString(this.randn_bm(1, 200, 7)),
                    author: name,
                    ...(rnd() > 0.5 ? {} : { group: "Beispielgruppe" }),
                    ...(rnd() > 0.5 ? {} : { likes: rndInt(100) }),
                    ...(rnd() > 0.5
                      ? {}
                      : {
                          reactions: range()
                            .map(() => ["Like", "Love", "Haha", "Wow", "Sad", "Angry"][rndInt(100) % 6])
                            .filter(() => rnd() > 0.5),
                        }),
                  },
                },
              ],
              title: `${name} hat einen Beitrag veröffentlicht.`,
            }))(rndDate(7)),
          ),
        },
        toText: (data) => toJSON(data).then((json) => `window.YTD.posts.part0 = ${json}`),
      },
      {
        name: "JSON/uri/image-meta.json",
        data: {
          starred: false,
          viewers_can_download: true,
          editors_can_edit_access: true,
          ...((date) => ({
            last_modified_by_any_user: addTime(date, 1000 * 3600 * 24 * 60).toISOString(),
            last_modified_by_me: addTime(date, 1000 * 3600 * 24 * 30).toUTCString(),
            content_last_modified: Math.round(addTime(date, 1000 * 3600 * 24 * 60).getTime() / 1000),
            created: `${date.getFullYear()}/${
              date.getMonth() + 1
            }/${date.getDate()} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`,
          }))(rndDate(3)),
          permissions: [
            {
              id: `${rndInt()}`,
              name,
              type: "user",
              role: "owner",
              kind: "drive#permission",
              photo_link: "JSON/uri/photo.png",
              email_address: email(name),
              domain: "gmail.com",
              etag: `"${rndInt()}"`,
              deleted: false,
            },
          ],
        },
        toText: toJSON,
      },
      {
        name: "JSON/uri/photo.png",
        data: `${window.location.origin}/preview.png`,
        toText: (data) => fetch(String(data)).then((result) => result.blob() as unknown as string),
      },
    ];

    // Generate Files and ZIP
    const zip = new JSZip();
    for (const f of sampleData) {
      zip.file(f.name, await f.toText(f.data));
    }

    return zip.generateAsync({ type: "blob" });
  }

  static generate(seed = 0, lang: LanguageTypes = "en"): ProviderFile[] {
    if (seed === 0) {
      seed = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
    } else {
      seed = Math.floor(seed);
    }

    this.instance.s = seed + 1;
    const t = translation[lang];

    const random = (): number => {
      return this.instance.random();
    };

    const random_array = <T>(array: T[]): T => {
      return this.instance.random_array(array);
    };

    const randn_bm = (min: number, max: number, skew: number) => {
      return this.instance.randn_bm(min, max, skew);
    };

    const getName = (): string => {
      return this.instance.getName();
    };

    // "Your full name is Max Munstermann
    // Your first name is Max
    // Your last name is Munstermann
    // Your current email is max.mustermann@igd.fraunhofer.de
    // Your birthday is on Freitag, 29. Juli 1994
    // Your gender is MALE
    // Your pronoun is MALE
    // Your education experiences are Universität zu Köln (Graduated, school type: Hochschule) and Seoul National University (Graduated, school type: Schule)
    // Your pages are Other (Köln)
    // Your groups are Köln Kleinanzeigen (30. Juli 2019) and Beste Jobs in Köln und Umgebung (29. Juli 2019)
    // Your registration timestamp is 29. Juli 2019
    // Your profile uri is https://www.facebook.com/profile.php?id=100040153803145",

    function generatePerson(): Person {
      const name = getName();
      const birthday = randomDate(
        new Date(new Date().getFullYear() - 18 - Math.floor(random() * 70), 0, 1),
        new Date(),
      );

      return {
        name,
        age: new Date().getFullYear() - birthday.getFullYear(),
        gender: getPronoun(),
        email: getEmail(name),
        birthday,
        city: random_array(cities),
      };
    }

    function getProfileInformation(me: Person, peopleStack: Person[]): string {
      let result = "";
      result += t.name + me.name;
      result += t.first + me.name.substring(0, me.name.indexOf(" "));
      result += t.middle;
      result += t.last + me.name.substring(me.name.indexOf(" ") + 1);
      result += t.current + me.email;
      result += t.previousemail + me.name.replace(" ", "_") + "@facebook.com";
      result += t.pending;
      result += t.ad;
      result += t.city + me.city;
      result += t.birthday + me.birthday.toLocaleDateString();
      result += t.gender + me.gender;
      result += t.pronoun + me.gender;
      result += t.previousnames + me.name + "1";
      result += String(t.other);
      result +=
        t.family +
        peopleStack[Math.floor(peopleStack.length * random())].name +
        ", " +
        peopleStack[Math.floor(peopleStack.length * random())].name +
        t.and +
        peopleStack[Math.floor(peopleStack.length * random())].name;
      return result;
    }

    const randomDate = (start: Date, end: Date) => {
      return this.instance.randomDate(start, end);
    };

    function getImageName() {
      const a = Math.floor(random() * 9999);
      const b = Math.floor(random() * 999);
      const c = Math.floor(random() * 99);
      return a + "_" + b + "_o_" + c + ".jpg";
    }

    function getVideoName() {
      const a = Math.floor(random() * 9999);
      const b = Math.floor(random() * 999);
      const c = Math.floor(random() * 99);
      return a + "_" + b + "_o_" + c + ".mp4";
    }

    function generateImageFile() {
      // 10kb to 1mb
      const title = random() > 0.3 ? "/mobile_uploads" : random() > 0.5 ? "/your_posts" : "/youralbum";
      return getFileObject(
        getImageName(),
        "photos_and_videos" + title,
        FileCategory.Picture,
        randn_bm(1024 * 10, 1024 * 1024 * 1, 5),
        [],
      );
    }
    function generateVideoFile() {
      // 10kb to 1mb
      return getFileObject(
        getVideoName(),
        "photos_and_videos",
        FileCategory.Video,
        randn_bm(1024 * 100, 1024 * 1024 * 15, 5),
        [],
      );
    }

    function generateMessageFile(diffTime: number, me: string, person: Person) {
      let sequence = generateSequences(diffTime, 50, 5, new Date(2004, 1).getTime());

      // First dense spot
      let begin_dense = new Date(2012, 8, 7).getTime() + 17 * 60 * 60 * 1000 + 23 * 60 * 1000; //17:23
      let end_dense = new Date(2012, 8, 10).getTime() + 23 * 60 * 60 * 1000 + 51 * 60 * 100; // 23:51
      sequence = modulateDenseTimes(sequence, begin_dense, end_dense, 10, 5);

      //second dense spot
      begin_dense = new Date(2008, 7, 7).getTime() + 15 * 60 * 60 * 1000 + 3 * 60 * 1000; //15:03
      end_dense = new Date(2009, 8, 10).getTime() + 13 * 60 * 60 * 1000 + 7 * 60 * 100; // 13:07
      sequence = modulateDenseTimes(sequence, begin_dense, end_dense, 10, 7);

      return fillPointsWithContent(person, sequence, Category.Messages, me);
    }

    const getString = (len: number) => {
      return this.instance.getString(len);
    };

    function getEmail(name: string) {
      return name.replace(" ", "@") + ".com";
    }

    function getPronoun() {
      return random() > 0.1 ? (random() > 0.5 ? t.male : t.female) : t.non;
    }

    function getFolderName(name: string) {
      return name.replace(" ", "");
    }

    const getDate = (diffTime: number) => {
      return this.instance.getDate(diffTime);
    };

    function generateSequences(diffTime: number, maxSequenceLength: number, density: number, startDatePoints: number) {
      let sequences_array = [];

      // Generate multiple sequences
      for (let i = 0; i < density; ++i) {
        let point_array = [];
        const sequenceStartPoint = generatePointInTime(diffTime, startDatePoints);
        point_array = generateOneSequence(sequenceStartPoint, maxSequenceLength);
        for (const element of point_array) {
          sequences_array.push(element);
        }
      }

      // Can be also applied outside of the method
      // Modulate Night patterns
      sequences_array = modulatePatterns(sequences_array);

      //add weekends
      sequences_array = modulateWeekends(sequences_array);

      sequences_array = modulateDaysOffline(
        sequences_array,
        new Date(2014, 4, 12).getTime(),
        new Date(2015, 7, 12).getTime(),
      );
      sequences_array = modulateDaysOffline(
        sequences_array,
        new Date(2010, 8, 23).getTime(),
        new Date(2010, 1, 24).getTime(),
      );
      sequences_array = modulateDaysOffline(
        sequences_array,
        new Date(2010, 2, 23).getTime(),
        new Date(2010, 4, 24).getTime(),
      );
      sequences_array = modulateDaysOffline(
        sequences_array,
        new Date(2020, 2, 23).getTime(),
        new Date(2020, 5, 24).getTime(),
      );

      return sequences_array;
    }

    function generateOneSequence(sequenceStartPoint: number, maxSequenceLength: number) {
      const point_array = [];
      //for each sequence choose a random length within range
      const randnum = randn_bm(0.1, 1, 1.3);
      // let sequence_length = Math.round(random()*maxSequenceLength);
      const sequence_length = Math.round(randnum * maxSequenceLength);
      const distances = [1000, 60 * 1000, 60 * 60 * 1000, 60 * 60 * 1000 * 10];
      const distance_dimension = distances[Math.round(randn_bm(0, 5, 1.3))];

      // Generate points for given category
      for (let j = 0; j < sequence_length; j++) {
        //Adjust distances of the points with random values
        //MS - TODO: outsource as parameter
        const randnum1 = randn_bm(0.1, 1, 1.3);
        const point_distance = Math.round(randnum1 * distance_dimension);
        const timeOfPoint = sequenceStartPoint + j * point_distance;
        //from begining a message after random distance

        point_array.push(timeOfPoint);
      }

      return point_array;
    }

    function modulatePatterns(timePointArray: number[]) {
      const timePointSequenceModulated = [];

      // APPLY SLEEP
      for (const element of timePointArray) {
        // person should be sleeping, meaning less activity between 0 and 8 so we skip them
        const hours = toHours(toLocalTime(new Date(element)));
        if (hours < randn_bm(5, 10, 4) && random() > 0.08) {
          // Ignore or move to other time
        } else if (hours >= randn_bm(22, 24, 1.3) && random() > 0.03) {
          // Evening a little bit easier
          // Ignore or make something special
        } else {
          timePointSequenceModulated.push(element);
        }
      }
      return timePointSequenceModulated;
    }

    function modulateWeekends(timePointArray: number[]) {
      const timePointSequenceModulated = [];

      for (const element of timePointArray) {
        //Apply work-Days/weekend-Days pattern
        const day = new Date(element).getDay();
        //On week ends
        if (day === 7 || (day === 0 && random() > 0.05)) {
          //do something special or let it empty
        } else timePointSequenceModulated.push(element);
      }
      return timePointSequenceModulated;
    }

    // No Data for selected days
    function modulateDaysOffline(timePointArray: number[], startDateOffline: number, endDateOffline: number) {
      const timePointSequenceModulated = [];
      for (const element of timePointArray) {
        if (new Date(element).getTime() >= startDateOffline && new Date(element).getTime() <= endDateOffline) {
          // do nothing
        } else {
          timePointSequenceModulated.push(element);
        }
      }
      return timePointSequenceModulated;
    }

    function modulateDenseTimes(
      sequence: number[],
      startDateDense: number,
      endDateDense: number,
      density: number,
      maxSequenceLengt: number,
    ) {
      const modulated_sequence = sequence;
      const dense_sequence = generateSequences(
        endDateDense - startDateDense,
        maxSequenceLengt,
        density,
        startDateDense,
      );
      for (const element of dense_sequence) {
        modulated_sequence.push(element);
      }
      return modulated_sequence;
    }

    function fillPointsWithContent(person: Person, timePointSequence: number[], category: string, me: string) {
      const Stack = [];
      const name = person.name;
      // CATEGORIES
      //Messages
      if (category === Category.Messages) {
        for (const element of timePointSequence) {
          const text = (random() < 0.5 ? name : me) + ": " + getString(randn_bm(1, 200, 7));
          const sub = t.ChatWith + name + t.and + me;
          const point_content = toDataPoint(new Date(element), text, Category.Messages, sub, undefined, [name, me]);
          Stack.push(point_content);
        }
        Stack.sort((a, b) => a.date.getTime() - b.date.getTime());
        return getFileObject(
          "message_1.json",
          "messages/" + getFolderName(name),
          FileCategory.Text,
          128 * Stack.length * 2,
          Stack,
        );
      }

      throw new Error("Unknown Category: " + category);
    }

    /**
     * Returns a point in time (date & time) in ticks
     * Generates a new random date within the given timerange
     * Moduled by random day and random time
     * comparable to former generateDate
     *
     * @param diffTime
     * @param startDatePoints
     * @returns number
     */
    function generatePointInTime(diffTime: number, startDatePoints: number) {
      const ticks_day = 1000 * 60 * 60 * 24;
      const startDate_intern = startDatePoints - 60 * 60 * 1000;
      // const startDate_intern = new Date("2004").getTime() - 60*60*1000;
      //Day
      const newday = random() * diffTime;
      const current_day = newday - 60 * 60 * 1000 - (newday % ticks_day);

      //Time
      const newRandomTime = random() * diffTime;
      const current_hour = new Date(newRandomTime).getHours();
      const current_minutes = new Date(newRandomTime).getMinutes();
      const current_seconds = new Date(newRandomTime).getSeconds();
      const newtime = current_hour * 60 * 60 * 1000 + current_minutes * 60 * 1000 + current_seconds * 1000;

      let result = startDate_intern + current_day + newtime;

      // person should be sleeping, meaning less activity between 0 and 8 so we skip them
      const hours = toHours(toLocalTime(new Date(result)));
      if (hours < 7 && random() > 0.08) result += 1000 * 60 * 60 * (6 + 2 * random());
      else if (hours > 22 && random() > 0.04) {
        result += 1000 * 60 * 60 * (3 + 3 * random());
      }

      return result;
    }

    function toHours(d: number) {
      return Math.floor((d / (1000 * 60 * 60)) % 24);
    }

    function toLocalTime(d: Date) {
      return d.getTime() - d.getTimezoneOffset() * 60000;
    }

    function getFileObject(
      fileName: string,
      folder: string,
      fileType: FileCategory,
      fileSize: number,
      data: DataPoint[],
    ): ProviderFile {
      return {
        compressedSize: Math.floor(fileSize + 1),
        fileName,
        zipName: "",
        dataCategory: data.length === 0 ? Category.Other : data[0].category,
        fileType,
        folder,
        hash: "",
        date: new Date(),
        isDir: false,
        path: folder + "/" + fileName,
        data: data.filter((d) => d.date.getFullYear() > 1970 && d.date.getFullYear() <= new Date().getFullYear() + 1),
        uncompressedSize: Math.floor(1 + fileSize * Math.max(1, 0.9 + random() / 4)),
        groupBy: "folder",
        providerId: "sample",
        id: fileCounter++,
        binaryData: fileName.endsWith("jpg") ? pictures[Math.floor(random() * pictures.length)] : null,
        exportId: -1,
        mimeType: "unknown/unknown",
        parserData: {
          structure: undefined,
          byteData: undefined,
          algoStep: AlgoStep.ExtractData,
        },
      };
    }

    let fileCounter = 0;
    const objectStack: ProviderFile[] = [];
    const startDate = new Date(2004, 1).getTime();
    const endDate = new Date(2022, 10, 12).getTime();
    const diffTime = endDate - startDate;
    const me = generatePerson();
    const peopleStack = [];
    const peopleCount = randn_bm(10, 100, 1);
    for (let j = 0; j < peopleCount; ++j) {
      peopleStack.push(generatePerson());
    }

    for (const file in facebookBlueprint) {
      switch (facebookBlueprint[file][0]) {
        case "your_address_books.json": {
          const messagesStack = [];
          let size = 0;
          for (let i = 0; i < peopleCount; i = i + 2) {
            const str = peopleStack[i].name;
            const message = toDataPoint(
              new Date(startDate + getDate(diffTime / 100)),
              str,
              Category.Contacts,
              t.AddressBook,
            );
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(message);
          }
          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject(
              "your_address_books.json",
              "friends/your_address_books.json",
              FileCategory.Text,
              size,
              messagesStack,
            ),
          );
          break;
        }
        case "location.json": {
          const messagesStack = [];
          let size = 0;
          let timePointSequence: number[] = [];
          for (
            let i = new Date(2018, 1).getTime();
            i < endDate;
            i = i + random() * 200 * 1000 * 60 * 10 // temporary factor to reduce number of location points
          ) {
            timePointSequence.push(i);
          }

          timePointSequence = modulatePatterns(timePointSequence);
          timePointSequence = modulateDaysOffline(
            timePointSequence,
            //TODO use dynamic dates
            new Date(2020, 2, 23).getTime(),
            new Date(2020, 5, 24).getTime(),
          );

          for (let i = 0; i < timePointSequence.length; i++) {
            let coordinates = "";
            //WorkPlace
            if (
              new Date(timePointSequence[i]).getHours() > 8 &&
              new Date(timePointSequence[i]).getHours() < 18 &&
              new Date(timePointSequence[i]).getDay() >= 1 &&
              new Date(timePointSequence[i]).getDay() <= 5
            ) {
              coordinates = places.Fraunhofer.coordinates;
            } else if (
              //TODO use dynamic dates
              new Date(2019, 6, 1).getTime() > timePointSequence[i] &&
              i < new Date(2019, 7, 1).getTime()
            ) {
              // Vacation
              coordinates = places.Vacation.coordinates;
            } else {
              coordinates = places.Home.coordinates;
            }

            const longLatMatches = coordinates.match(/\((.+?),(.+?)\)/);
            let longLat;
            if (longLatMatches && longLatMatches?.length === 3) {
              longLat = [Number(longLatMatches?.[2]), Number(longLatMatches?.[1])] as [number, number];
            }

            const point_content = toDataPoint(
              new Date(timePointSequence[i]),
              coordinates,
              Category.Location,
              "Dein Standort",
              undefined,
              undefined,
              longLat,
            );
            size += (19 + 8 + 8 + coordinates.length) * 2;
            messagesStack.push(point_content);
          }

          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject("location.json", "location/location.json", FileCategory.Text, size, messagesStack),
          );
          break;
        }

        case "friends.json": {
          const messagesStack = [];
          let size = 0;
          let timePointSequence = generateSequences(diffTime, 3, 400, new Date(2004, 1).getTime());
          timePointSequence = modulateDenseTimes(
            timePointSequence,
            new Date(2004, 4, 3).getTime(),
            new Date(2004, 8, 5).getTime(),
            30,
            4,
          );

          for (let i = 0; i < peopleCount; ++i) {
            const str = peopleStack[i].name;
            const point_content = toDataPoint(new Date(timePointSequence[i]), str, Category.Contacts, t.Friend);
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(point_content);
          }
          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject("friends.json", "friends/friends.json", FileCategory.Text, size, messagesStack),
          );
          break;
        }
        case "sent_friend_requests.json":
          break;
        case "pages.json": {
          const messagesStack = [];
          let size = 0;
          const timePointSequence = generateSequences(diffTime, 7, 700, new Date(2004, 1).getTime());

          for (const element of timePointSequence) {
            const str = random() > 0.5 ? getString(1) : getName();
            const point_content = toDataPoint(new Date(element), t.liked + str, Category.Likes, t.PageLikes);
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(point_content);
          }
          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject("pages.json", "likes_and_reactions/pages.json", FileCategory.Text, size, messagesStack),
          );
          break;
        }
        case "posts_and_comments.json": {
          // {"date": Date,"text": "Max Munstermann gefällt Patrick ******* Beitrag in Köln Kleinanzeigen.","category": "Likes","subcategory": "Post and Comment Likes"}
          const messagesStack = [];
          let size = 0;

          let timePointSequence = generateSequences(diffTime, 7, 100, new Date(2004, 1).getTime());
          const begin_dense = new Date(2010, 8, 7).getTime() + 17 * 60 * 60 * 1000 + 23 * 60 * 1000; //17:23
          const end_dense = new Date(2010, 8, 7).getTime() + 23 * 60 * 60 * 1000 + 51 * 60 * 100; // 23:51
          timePointSequence = modulateDenseTimes(timePointSequence, begin_dense, end_dense, 5, 5);

          for (const element of timePointSequence) {
            const str = peopleStack[Math.floor(peopleStack.length * random())].name;
            const point_content = toDataPoint(new Date(element), t.post + str, Category.Likes, t.Postings);
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(point_content);
          }
          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject(
              "posts_and_comments.json",
              "likes_and_reactions/posts_and_comments.json",
              FileCategory.Text,
              size,
              messagesStack,
            ),
          );
          break;
        }
        case "your_posts_1.json": {
          // {"date": Date, "text": "Max Mustermann hat eine Erinnerung geteilt.", "category": "Posts", "subcategory": "Your Posts"}
          const timePointSequence = generateSequences(diffTime, 7, 20, new Date(2004, 1).getTime());
          // return fillPointsWithContent (person, sequence, Category.Messages, me);
          const messagesStack = [];
          let size = 0;

          for (const element of timePointSequence) {
            const str = t.posted + getString(randn_bm(20, 200, 1));
            const point_content = toDataPoint(new Date(element), str, Category.PostsAndComments, t.YourPosts);
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(point_content);
          }
          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject("your_posts_1.json", "posts/your_posts_1.json", FileCategory.Text, size, messagesStack),
          );
          break;
        }
        case "profile_information.json": {
          const messagesStack = [];
          let size = 0;
          const str = getProfileInformation(me, peopleStack);
          const message = toDataPoint(new Date(startDate), str, Category.Account, t.Profile);
          size += (19 + 8 + 8 + str.length) * 2;
          messagesStack.push(message);
          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject(
              "profile_information.json",
              "profile_information/profile_information.json",
              FileCategory.Text,
              size,
              messagesStack,
            ),
          );
          break;
        }
        case "profile_update_history.json": {
          // {"date": Date,"text": "Max Munstermann hat „Aktueller Wohnort“ zu Köln, Deutschland geändert.","category": "Account","subcategory": "Profile Updates"}
          const messagesStack = [];

          const timePointSequence = generateSequences(diffTime, 4, 10, new Date(2004, 1).getTime());
          let size = 0;

          for (const element of timePointSequence) {
            const str = me.name + t.placeChange + random_array(cities);
            const point_content = toDataPoint(new Date(element), str, Category.Account, t.ProfileUpdate);
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(point_content);
          }
          /*
           for (let i = 0; i < times; ++i) {
               let str = me.name + t.placeChange + random_array(cities);
               let message = toDataPoint(new Date(startDate + getDate(diffTime)), str, Category.Account, t.ProfileUpdate);
               size += (19 + 8 + 8 + str.length) * 2;
               messagesStack.push(message);
           }
           */

          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject(
              "profile_update_history.json",
              "search_history/profile_update_history.json",
              FileCategory.Text,
              size,
              messagesStack,
            ),
          );
          break;
        }
        case "your_search_history.json": {
          // {"date": Date,"text": "Du hast nach „köln“ gesucht","category": "Activity","subcategory": "köln"}
          const messagesStack = [];
          let size = 0;
          const timePointSequence = generateSequences(diffTime, 20, 800, new Date(2004, 1).getTime());
          for (const element of timePointSequence) {
            const str = random() > 0.5 ? getString(random() > 0.5 ? 1 : 2) : getName();
            const point_content = toDataPoint(
              new Date(element),
              t.searched + " „" + str + "“",
              Category.Activity,
              "Search",
            );
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(point_content);
          }
          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject(
              "your_search_history.json",
              "search_history/your_search_history.json",
              FileCategory.Text,
              size,
              messagesStack,
            ),
          );
          break;
        }
        case "account_activity.json":
          // {"date": Date,"text": "1.2.3.4","category": "Security","subcategory": "Security: Email Added"},
          //let types = ["Session updated", "Login", "Email added", "Checkpoint"];
          break;
        case "administrative_records.json":
          // {"date": Date, "text": "Email change", "category": "Security", "subcategory": "Security: Admin"}
          break;
        case "logins_and_logouts.json": {
          // {"date": Date, "text": "Anmelden www.facebook.com", "category": "Security", "subcategory": "Security: Login and Logout"}
          const messagesStack = [];
          let size = 0;

          let timePointSequence = generateSequences(diffTime, 5, 400, new Date(2004, 1).getTime());
          timePointSequence = modulateDaysOffline(
            timePointSequence,
            new Date(2004, 1).getTime(),
            new Date(2017, 1).getTime(),
          );

          for (const element of timePointSequence) {
            const str = random() > 0.5 ? t.signin : t.signout;
            const point_content = toDataPoint(new Date(element), str, Category.Security, t.Security);
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(point_content);
          }

          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject(
              "logins_and_logouts.json",
              "security_and_login_information/logins_and_logouts.json",
              FileCategory.Text,
              size,
              messagesStack,
            ),
          );

          break;
        }
        case "login_protection_data.json": {
          // {"date": Date, "text": "Cookie: ************************", "category": "Security", "subcategory": "Security: Login Protection"}
          const times = randn_bm(20, 500, 2.5);
          const messagesStack = [];
          let size = 0;
          for (let i = 0; i < times; ++i) {
            const str = random() > 0.5 ? "Cookie: 31**********************" : "Cookie: 89**********************";
            const message = toDataPoint(
              new Date(startDate + (diffTime / 4) * 3 + (random() * diffTime) / 4),
              str,
              Category.Security,
              t.Login,
            );
            size += (19 + 8 + 8 + str.length) * 2;
            messagesStack.push(message);
          }
          messagesStack.sort((a, b) => a.date.getTime() - b.date.getTime());
          objectStack.push(
            getFileObject(
              "login_protection_data.json",
              "security_and_login_information/login_protection_data.json",
              FileCategory.Text,
              size,
              messagesStack,
            ),
          );
          break;
        }
        case "where_you're_logged_in":
          // {"date": Date, "text": "Chrome on Windows PC in Frankfurt, Germany", "category": "Security", "subcategory": "Security: Active Logins"}
          break;
        default:
          break;
      }
    }

    // messages
    // {"date": Date,"text": "Max Munstermann: Sehr kreativer Name","category": "Messages","subcategory": "Chat with Max Munstermann and Max Munstermann"}
    for (const element of peopleStack) {
      objectStack.push(generateMessageFile(diffTime, me.name, element));
    }
    const images = randn_bm(20, 200, 1);
    for (let j = 0; j < images; ++j) {
      objectStack.push(generateImageFile());
    }
    objectStack.push(getFileObject(getImageName(), "profile_photo", FileCategory.Picture, 2670016, []));
    const videos = randn_bm(3, 20, 1);
    for (let j = 0; j < videos; ++j) {
      objectStack.push(generateVideoFile());
    }
    return objectStack;
  }
}
